import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navItems } from "@/mocks/site";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50 border-b border-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-lg font-heading font-semibold">
              久
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-semibold text-foreground-950 tracking-wide">
                国产久久久
              </span>
              <span className="text-[11px] text-foreground-500 tracking-widest">
                GUOCHAN · 影视资讯
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive(item.path)
                    ? "text-primary-600 font-medium bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden md:flex items-center gap-2 h-9 rounded-md border border-background-300 bg-background-100 px-3 focus-within:border-primary-400 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、电视剧、资讯"
                className="w-44 xl:w-56 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
            </form>

            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="打开菜单"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer hover:bg-background-100"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <aside className="absolute right-0 top-0 h-full w-72 max-w-[80%] bg-background-50 shadow-none p-5 flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <span className="font-heading text-lg font-semibold text-foreground-950">
                菜单
              </span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭菜单"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 cursor-pointer hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 h-10 rounded-md border border-background-300 bg-background-100 px-3 mb-5"
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片、电视剧、资讯"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
            </form>

            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${
                    isActive(item.path)
                      ? "bg-primary-50 text-primary-600 font-medium"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  {item.label}
                </Link>
              ))}
            </nav>

            <p className="mt-auto text-xs text-foreground-400 leading-relaxed">
              国产久久久 · 专注国产影视作品与影视资讯的内容平台
            </p>
          </aside>
        </div>
      )}
    </header>
  );
}